import React, { createContext, useMemo } from 'react';
import { ComponentProps } from './HotelInformation.types';
import { Styled } from './HotelInformation.styles';
import { Information } from './Information/index';
import { Amenities } from './Amenities/index';
import { PropertyDetails } from './PropertyDetails/index';
import { useConditionalPageContextSERP } from '../../hooks';
import { useHQVDatalayerTrackingStore } from '../../modules/store/hqvDatalayerTrackingStore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const serverDataProps: any = {
  hotelInformationData: {},
  propertyId: '',
  jsonContent: {},
  amenitiesData: {},
  accessibilityData: {},
  generalInformationData: {},
  brandCode: '',
};

export const serverData = createContext(serverDataProps);

export const HotelInformation: React.FC<ComponentProps> = ({
  hotelInformationData,
  propertyId,
  jsonContent,
  amenitiesData,
  accessibilityData,
  generalInformationData,
  brandCode,
  keyAmenities,
  pageProps,
  isAuthorMode,
}) => {
  const value: ComponentProps = useMemo(
    () => ({
      hotelInformationData,
      propertyId,
      jsonContent,
      amenitiesData,
      accessibilityData,
      generalInformationData,
      brandCode,
      keyAmenities,
      pageProps,
      isAuthorMode,
    }),
    [
      accessibilityData,
      amenitiesData,
      brandCode,
      generalInformationData,
      hotelInformationData,
      jsonContent,
      propertyId,
      keyAmenities,
      pageProps,
      isAuthorMode,
    ]
  );

  const { pageVal } = useConditionalPageContextSERP();

  // Use propertyInfoStatus to determine if information section should be shown
  // 'success' means PropertyInfo data is available (fresh or cached)
  // 'error' means PropertyInfo call failed AND no cached data exists
  const propertyInfoStatus = useHQVDatalayerTrackingStore(state => state.propertyInfoStatus);

  // Use hqvFallbackMode to determine if we're in amenities_only mode
  // In amenities_only mode, hide Information section (both SWR and legacy mode)
  const hqvFallbackMode = useHQVDatalayerTrackingStore(state => state.hqvFallbackMode);

  // Show information section when:
  // - Not disabled by AEM config
  // - In author mode (always show for authoring)
  // - NOT in amenities_only or closed fallback mode (both SWR and legacy)
  // - PropertyInfo data is available (fresh or cached) - status will be 'success' if data exists
  // - Special case for AI Chat (pageVal === 'chat'): show when 'idle' because Chat doesn't set propertyInfoStatus
  const isChat = pageVal === 'chat';
  const shouldShowInformation =
    !jsonContent?.['isHotelInformationSectionDisabled'] &&
    (isAuthorMode ||
      (hqvFallbackMode !== 'amenities_only' &&
        hqvFallbackMode !== 'closed' &&
        (propertyInfoStatus === 'success' || (isChat && propertyInfoStatus === 'idle'))));

  return (
    <serverData.Provider value={value}>
      <Styled className="standard">
        {pageVal === 'chat' ? (
          <>
            {!jsonContent?.['isAmenitiesSectionDisabled'] && <Amenities isAuthorMode={isAuthorMode} />}
            {shouldShowInformation && <Information />}
            {!jsonContent?.['isPropertyDetailsSectionDisabled'] && <PropertyDetails />}
          </>
        ) : (
          <>
            {shouldShowInformation && <Information />}
            {!jsonContent?.['isAmenitiesSectionDisabled'] && <Amenities isAuthorMode={isAuthorMode} />}
            {!jsonContent?.['isPropertyDetailsSectionDisabled'] && <PropertyDetails />}
          </>
        )}
      </Styled>
    </serverData.Provider>
  );
};

export default HotelInformation;
